import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  styled,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import type { StandingOrderContext } from '../machines/standingOrder.machine';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import { TripLegType } from '@/data/models/reservation.model';

const PREFIX = 'StandingOrderForm';

const classes = {
  container: `${PREFIX}-container`,
  standingOrderDescription: `${PREFIX}-standingOrderDescription`,
  standingOrderGrid: `${PREFIX}-standingOrderGrid`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  [`& .${classes.standingOrderDescription}`]: {
    color: theme.palette.lightTextSecondary.main,
  },
  [`& .${classes.standingOrderGrid}`]: {
    maxWidth: 500,
    marginTop: theme.spacing(2),
  },
}));

interface StandingOrderFormProps {
  index: number;
}

export function StandingOrderForm({ index }: StandingOrderFormProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { register, control, setValue } = useFormContext<any>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs.standingOrder;
  useFieldActorSnapshot(actor);

  const fieldName = `trips.${index}.standingOrder` as const;
  const standingOrderRaw = useWatch({
    control: control as any,
    name: fieldName as any,
  });
  const standingOrderValue = !!standingOrderRaw;

  const tripType = useWatch({
    control: control as any,
    name: 'tripType' as any,
  }) as unknown as TripLegType | undefined;
  
  const daysOfWeek = [
    { key: 'monday', label: t('reservation.monday') },
    { key: 'tuesday', label: t('reservation.tuesday') },
    { key: 'wednesday', label: t('reservation.wednesday') },
    { key: 'thursday', label: t('reservation.thursday') },
    { key: 'friday', label: t('reservation.friday') },
    { key: 'saturday', label: t('reservation.saturday') },
    { key: 'sunday', label: t('reservation.sunday') },
  ] as const;

  const enabledValues = useWatch({
    control: control as any,
    name: daysOfWeek.map(
      (day) =>
        `trips.${index}.standingOrderDays.${day.key}.enabled` as any,
    ) as any,
  }) as boolean[] | undefined;

  const returnTypeValues = useWatch({
    control: control as any,
    name: daysOfWeek.map(
      (day) =>
        `trips.${index}.standingOrderDays.${day.key}.returnType` as any,
    ) as any,
  }) as (string | undefined)[] | undefined;

  const roundTripWillCall = useWatch({
    control: control as any,
    name: `trips.${index}.roundTripWillCall` as any,
  }) as unknown as boolean | undefined;

  useEffect(() => {
    const snapshot = actor.getSnapshot().context as StandingOrderContext;
    if (snapshot.values[index] !== standingOrderValue) {
      actor.send({ type: 'CHANGE', index, value: standingOrderValue });
    }
  }, [actor, index, standingOrderValue]);

  useEffect(() => {
    if (tripType !== TripLegType.RoundTrip) return;
    if (roundTripWillCall === undefined) return;

    const newReturnType = roundTripWillCall ? 'WillCall' : 'Time';

    daysOfWeek.forEach((day, dayIndex) => {
      const enabled = !!enabledValues?.[dayIndex];
      if (!enabled) return;

      const baseField = `trips.${index}.standingOrderDays.${day.key}` as const;

      setValue(`${baseField}.returnType` as any, newReturnType, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, [
    tripType,
    roundTripWillCall,
  ]);

  return (
    <StyledBox className={classes.container}>
      <FormControl component="fieldset" variant="standard">
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              {...register(fieldName)}
              sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
            />
          }
          label={t('reservation.standingOrder')}
        />
        {standingOrderValue && (
          <Typography variant="body2" className={classes.standingOrderDescription}>
            {t('reservation.standingOrderDescription')}
          </Typography>
        )}
        
        {standingOrderValue && (
          <Grid
            container
            columnSpacing={2}
            rowSpacing={1}
            alignItems="flex-start"
            className={classes.standingOrderGrid}>
            <Grid item xs={4}>
              {t('reservation.days')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.appointment')}
            </Grid>
            <Grid item xs={4}>
              {t('reservation.pickup')}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {daysOfWeek.map((day, dayIndex) => {
              const baseField = `trips.${index}.standingOrderDays.${day.key}` as const;
              const enabled = !!enabledValues?.[dayIndex];
              const returnType = returnTypeValues?.[dayIndex];

              return (
                <Grid item container xs={12} key={day.key} columnSpacing={2}>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          {...register(`${baseField}.enabled` as const)}
                          sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
                        />
                      }
                      label={day.label}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.appointmentTime` as any}
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            label={t('reservation.appointment')}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value)}
                            disabled={tripType === TripLegType.RoundTrip}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    {enabled && (
                      <Controller
                        name={`${baseField}.pickupTime` as any}
                        control={control}
                        render={({ field }) => (
                          <TimePicker
                            label={t('reservation.pickup')}
                            value={field.value ?? null}
                            onChange={(value) => field.onChange(value)}
                            disabled={tripType === TripLegType.RoundTrip}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                  {tripType === TripLegType.RoundTrip && (
                    <>
                      <Grid item xs={4}></Grid>
                      <Grid item xs={4}>
                        {enabled && (
                          <Controller
                            name={`${baseField}.returnType` as any}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                fullWidth
                                select
                                label={t('reservation.returnType')}
                                value={field.value ?? 'WillCall'}
                                onChange={(event) => field.onChange(event.target.value)}
                              >
                                <MenuItem value="WillCall">
                                  {t('reservation.returnTypeWillCall')}
                                </MenuItem>
                                <MenuItem value="Time">
                                  {t('reservation.returnTypeTime')}
                                </MenuItem>
                              </TextField>
                            )}
                          />
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        {enabled && returnType === 'Time' && (
                          <Controller
                            name={`${baseField}.returnPickupTime` as any}
                            control={control}
                            render={({ field }) => (
                              <TimePicker
                                label={t('reservation.returnPickup')}
                                value={field.value ?? null}
                                onChange={(value) => field.onChange(value)}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}
      </FormControl>
    </StyledBox>
  );
}

